import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import { TemplatePicker } from '@/features/room';

interface EmptyStateProps {
    onAddRoom: () => void;
    onSelectTemplate: (templateId: string) => void;
}

export const EmptyState: FC<EmptyStateProps> = ({ onAddRoom, onSelectTemplate }) => {
    const { t } = useTranslation();

    return (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center relative max-w-md mx-auto min-h-[60vh] justify-center">
            {/* SVG Blueprint Illustration */}
            <div className="relative mb-6 flex items-center justify-center w-40 h-40">
                <svg width="160" height="160" viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-slate-200 dark:text-slate-800/60">
                    <rect x="15" y="15" width="130" height="130" rx="16" stroke="currentColor" strokeWidth="2" strokeDasharray="6 6" />
                    <rect x="35" y="35" width="90" height="90" rx="12" stroke="currentColor" strokeWidth="3" className="text-blue-500/20 dark:text-blue-500/10" />
                    <line x1="35" y1="80" x2="125" y2="80" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 4" className="text-slate-300 dark:text-slate-800" />
                    <line x1="80" y1="35" x2="80" y2="125" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 4" className="text-slate-300 dark:text-slate-800" />
                    <path d="M 35 142 L 35 150 M 125 142 L 125 150 M 35 146 L 125 146" stroke="currentColor" strokeWidth="1.5" className="text-blue-500/40" />
                    <path d="M 18 35 L 26 35 M 18 125 L 26 125 M 22 35 L 22 125" stroke="currentColor" strokeWidth="1.5" className="text-blue-500/40" />
                </svg>
                <motion.div
                    animate={{ y: [0, -8, 0], rotate: [0, 4, -4, 0] }}
                    transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                    className="absolute bg-gradient-to-tr from-blue-600 to-indigo-600 p-4 rounded-2xl text-white shadow-xl shadow-blue-500/20 dark:shadow-none"
                >
                    <Plus size={28} />
                </motion.div>
            </div>

            <h2 className="font-extrabold text-2xl text-slate-800 dark:text-slate-100 mb-2">{t('editor.empty.title', 'Проект пуст')}</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-6 text-sm max-w-xs">{t('editor.empty.subtitle', 'Создайте первую комнату, чтобы начать расчет сметы')}</p>

            {/* Template picker grid inline — TMPL-01 / UI-SPEC */}
            <div className="w-full mb-6">
                <TemplatePicker
                    onSelect={onSelectTemplate}
                    onSkip={onAddRoom}
                />
            </div>

            {/* Blank room CTA — relabeled per UI-SPEC (skip from TemplatePicker also creates blank room) */}
            <motion.button
                whileHover={{ scale: 1.02, transition: { repeat: 0 } }}
                whileTap={{ scale: 0.98 }}
                onClick={onAddRoom}
                className="w-full px-8 py-4 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-2xl font-bold text-base border border-slate-200 dark:border-slate-700 transition-all flex items-center justify-center gap-3"
            >
                <Plus size={20}/>
                {t('editor.empty.create_room_btn', 'Создать комнату')}
            </motion.button>
        </div>
    );
};
